import { watch } from "vue";

import { EngineKeys, PosClient } from "@smeetz/pos-client";
import { setTranslator } from "@smeetz/i18n";

import i18n from "@/lang";
import UserModule from "@/store/modules/user";
import POSConfigModule from "@/store/modules/pos-config";
import { LogService, LogLevel } from "@/services/logging";

let posClient: PosClient | null = null;
let initPromise: Promise<void> | null = null;
let groupSyncInstalled = false;

/**
 * Build-time variables that locate this deployment's Elasticsearch data. Every
 * Bitbucket deployment environment sets them to its own values, so nothing here
 * derives a URL or an index name from an environment name.
 */
const REQUIRED_BUILD_ENV = {
  VITE_SERVICES_API: import.meta.env.VITE_SERVICES_API,
  VITE_ELASTIC_PRODUCTS: import.meta.env.VITE_ELASTIC_PRODUCTS,
  VITE_ELASTIC_TICKETS: import.meta.env.VITE_ELASTIC_TICKETS,
  VITE_ELASTIC_CUSTOMERS: import.meta.env.VITE_ELASTIC_CUSTOMERS,
  VITE_ELASTIC_BOOKING: import.meta.env.VITE_ELASTIC_BOOKING,
  VITE_ELASTIC_BOOKING_STATEMENTS: import.meta.env.VITE_ELASTIC_BOOKING_STATEMENTS,
  VITE_ELASTIC_TIME_SLOT: import.meta.env.VITE_ELASTIC_TIME_SLOT,
} as const;

const missingBuildEnv = Object.keys(REQUIRED_BUILD_ENV).filter(
  (name) => !REQUIRED_BUILD_ENV[name as keyof typeof REQUIRED_BUILD_ENV]
);

/**
 * Index name per engine for this deployment.
 *
 * Waivers are absent on purpose: the modelkit exposes no waiver engine, so
 * `VITE_ELASTIC_WAIVERS` stays with the legacy app-search path in
 * `src/app-search/services/request-elasticsearch.service.ts`.
 */
const ELASTICSEARCH_INDEXES: Partial<Record<EngineKeys, string>> = {
  [EngineKeys.Product]: REQUIRED_BUILD_ENV.VITE_ELASTIC_PRODUCTS,
  [EngineKeys.Ticket]: REQUIRED_BUILD_ENV.VITE_ELASTIC_TICKETS,
  [EngineKeys.Customer]: REQUIRED_BUILD_ENV.VITE_ELASTIC_CUSTOMERS,
  [EngineKeys.Booking]: REQUIRED_BUILD_ENV.VITE_ELASTIC_BOOKING,
  [EngineKeys.BookingStatement]: REQUIRED_BUILD_ENV.VITE_ELASTIC_BOOKING_STATEMENTS,
  [EngineKeys.TimeSlot]: REQUIRED_BUILD_ENV.VITE_ELASTIC_TIME_SLOT,
};

/**
 * Initialize `@smeetz/pos-client` once per session: loads CDN i18n dictionary
 * for the host locale, bridges `@smeetz/i18n.t()` to pos's vue-i18n instance
 * (so DS components like SSearchTable / SDynamicFilter resolve their internal
 * keys), and initializes the Elasticsearch modelkit using the authenticated
 * user's `elasticSearchTokens` (so SSearchTable's BookingCollection can hit
 * the cluster).
 *
 * Must be called after `UserModule.fetchUser()` completes — tokens come from
 * the user response. Subsequent calls return the same promise (idempotent).
 *
 * @returns {Promise<void>} Resolves once i18n dictionaries are loaded and
 *   modelkit is wired.
 */
export function ensurePosClientInit(): Promise<void> {
  if (initPromise) {
    return initPromise;
  }

  if (missingBuildEnv.length > 0) {
    // Degrade search, never the till. A missing variable must not white-screen a
    // point of sale, and it must not quietly fall back to another environment's
    // indexes either - that fallback is exactly how sandbox ended up querying
    // customer_index_prod. Lists surface their own "not initialized" error.
    LogService.info(
      `[pos-client-init] Missing build-time variable(s): ${missingBuildEnv.join(", ")}. ` +
        "Elasticsearch-backed lists stay disabled for this session.",
      { missingBuildEnv },
      LogLevel.Error
    );

    initPromise = Promise.resolve();

    return initPromise;
  }

  const tokens = UserModule.getUser?.elasticSearchTokens;

  // Don't cache a token-less client: a navigation can reach here before
  // fetchUser has loaded the tokens, and the cache would serve it until reload.
  if (!tokens || Object.keys(tokens).length === 0) {
    return Promise.resolve();
  }

  const groupAccess = POSConfigModule.groupId ?? UserModule.getUserGroupId ?? 0;
  const locale = i18n.global.locale.value as string;

  const client = new PosClient({
    locale,
    serviceUrl: REQUIRED_BUILD_ENV.VITE_SERVICES_API,
    indexes: ELASTICSEARCH_INDEXES,
    groupAccess,
    elasticSearchTokens: tokens
      ? (tokens as unknown as Record<string, string>)
      : undefined,
  });

  posClient = client;

  initPromise = (async () => {
    try {
      await client.init();

      // Merge CDN dictionary on top of pos's existing lang JSON messages so
      // DS internal keys resolve while pos's own keys keep working.
      const messages = client.i18n?.current ?? {};

      i18n.global.mergeLocaleMessage(locale, messages);

      // Bridge @smeetz/i18n's `t()` to pos's vue-i18n. DS components import
      // `t` directly from @smeetz/i18n; without this they'd return the raw
      // key for every translation lookup.
      //
      // Cast through `any` to avoid TS2589 (excessively deep type instantiation)
      // caused by vue-i18n v11's complex overloaded generics on `t()`. Same
      // workaround used in src/utils/vue-helpers/orders.ts:42.
      const translate: (key: string) => string = (i18n.global as any).t;

      setTranslator(translate);
    } catch (err) {
      LogService.info("[pos-client-init] init failed", { err });
    }
  })();

  return initPromise;
}

/**
 * Force the modelkit to reinitialize for a new group access. Use after a
 * group switch when the same authenticated user changes which group's data
 * they want to query.
 *
 * @param {number | string} next - New group access id.
 * @returns {boolean} Sentinel.
 */
export function setPosClientGroupAccess(next: number | string): boolean {
  if (!posClient) {
    return true;
  }

  posClient.setGroupAccess(next);

  return true;
}

/**
 * Install a single reactive watcher on `POSConfigModule.groupId` that pushes
 * the new value into the pos-client whenever it changes. Solves the
 * fresh-login race where `ensurePosClientInit()` fires before the user picks
 * a POS config — at that point `groupId` is null and modelkit gets stuck on
 * `groupAccess: 0`. The watcher catches the subsequent POS-config selection
 * and reinitialises modelkit with the real group.
 *
 * Memory profile: one Vue 3 reactive watcher (one closure + one dep entry).
 * Zero work when groupId does not change. Lives for the entire session — not
 * stopped because there is nothing to scope it to (POS terminals stay open).
 * Guarded with `groupSyncInstalled` so a second call is a no-op (defensive
 * against HMR module re-evaluation duplicating watchers).
 *
 * Call once from `main.ts` after the store is registered (alongside
 * `setupPermissionGuard()`).
 *
 * @returns {boolean} Sentinel.
 */
export function setupPosClientGroupSync(): boolean {
  if (groupSyncInstalled) {
    return true;
  }

  groupSyncInstalled = true;

  watch(
    () => POSConfigModule.groupId,
    (next) => {
      if (!next) {
        return undefined;
      }

      setPosClientGroupAccess(next);

      return true;
    }
  );

  return true;
}
